// Modal shown when the user taps "Get it on Google Play" anywhere on the
// web app. Instead of deep-linking to the Play listing (which counts as a
// referrer install), we walk the user through finding the app organically
// — that boosts ASO / organic ranking on the Play Store.
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Search, Smartphone, Download, CheckCircle2 } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}

const STEPS: { icon: typeof Search; title: string; body: string }[] = [
  {
    icon: Smartphone,
    title: "Open Google Play Store",
    body: "On your Android phone, open the Play Store app.",
  },
  {
    icon: Search,
    title: "Search the exact name",
    body: "Type FitAuraAI - Try On Clothes in the search bar.",
  },
  {
    icon: Download,
    title: "Tap Install",
    body: "Pick the app by FitAura AI and tap Install.",
  },
  {
    icon: CheckCircle2,
    title: "Sign in & enjoy",
    body: "Open the app and continue with the same account.",
  },
];

export const HowToDownloadDialog = ({ open, onOpenChange }: Props) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm rounded-3xl">
        <DialogHeader className="text-left">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/30 to-accent/20 text-primary ring-1 ring-primary/30">
            <Smartphone className="h-6 w-6" />
          </div>
          <DialogTitle className="text-center">How to get the app</DialogTitle>
          <DialogDescription className="text-center">
            Search <span className="font-semibold text-foreground">FitAuraAI - Try On Clothes</span> on the Google Play Store.
          </DialogDescription>
        </DialogHeader>

        <ol className="mt-2 space-y-3">
          {STEPS.map((s, i) => (
            <li key={i} className="flex items-start gap-3 rounded-2xl border border-border/60 bg-background/60 p-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
                <s.icon className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold leading-tight">
                  <span className="mr-1 text-muted-foreground">{i + 1}.</span>
                  {s.title}
                </p>
                <p className="mt-0.5 text-[12px] leading-snug text-muted-foreground">{s.body}</p>
              </div>
            </li>
          ))}
        </ol>

        <Button
          className="mt-3 w-full rounded-full"
          onClick={() => onOpenChange(false)}
        >
          Got it
        </Button>
      </DialogContent>
    </Dialog>
  );
};
